import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from './apiService';
import { datadogLogs } from '@datadog/browser-logs';

// Custom event for teacher pace meter updates
export const TEACHER_PACE_METER_UPDATE_EVENT = 'teacher-pace-meter-update';

// Interface for the custom event detail
export interface TeacherPaceMeterUpdateEvent {
  paceScore: number;
  paceCategory: string;
}

// Interface for the teacher pace meter data
export interface TeacherPaceMeterData {
  classId: number | string;
  classStartTime: Date;
  classDurationSeconds: number;
  paceScore: number;
  paceCategory: string;
  isRedZone?: boolean;
  isDefault?: boolean;
  rawMetrics?: {
    P30_wps: number;
    P60_wps: number;
    P90_wps: number;
    PoD_wps: number;
  };
}


interface UseTeacherPaceMeterResult {
  paceMeterData: TeacherPaceMeterData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage teacher pace meter data
 * @param classId - The ID of the class to fetch pace meter data for
 * @param pollingInterval - Optional polling interval in milliseconds (default: 10000 - 10 seconds)
 * @returns Object containing pace meter data, loading state, error state, and refetch function
 */
const useTeacherPaceMeter = (
  classId: number | string,
  pollingInterval = 10000
): UseTeacherPaceMeterResult => {
  const [paceMeterData, setPaceMeterData] = useState<TeacherPaceMeterData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Use a ref to track if data is being fetched to prevent race conditions
  const isFetchingRef = useRef<boolean>(false);
  // Use a ref to store the previous data for comparison
  const prevDataRef = useRef<TeacherPaceMeterData | null>(null);

  // Function to dispatch custom event for teacher pace meter updates
  const dispatchTeacherPaceMeterUpdate = useCallback((paceScore: number, paceCategory: string) => {
    const event = new CustomEvent<TeacherPaceMeterUpdateEvent>(TEACHER_PACE_METER_UPDATE_EVENT, {
      detail: {
        paceScore,
        paceCategory
      }
    });
    document.dispatchEvent(event);
  }, []);

  // Function to fetch teacher pace meter data
  const fetchPaceMeterData = useCallback(async () => {
    // Prevent multiple concurrent fetches
    if (isFetchingRef.current) {
      return;
    }
    
    if (!classId) {
      setIsLoading(false);
      return;
    }

    try {
      isFetchingRef.current = true;
      
      // Only show loading indicator on initial load
      if (!paceMeterData) {
        setIsLoading(true);
      }
            
      
      const response = await api.getTeacherPaceMeter(classId);
      
      if (response && response.data) {
        try {
          // Process dates to ensure they are Date objects
          let data = response.data;
          
          // Handle nested data structure if present
          if (data.data && data.data.data) {
            data = data.data.data;
          } else if (data.data) {
            data = data.data;
          }
          
          // Check if we have valid data from the API
          if (data) {
            // Process real data
            const processedData: TeacherPaceMeterData = {
              ...data,
              classId,
              classStartTime: new Date(data.classStartTime)
            };
            
            // Check if we need to update the state or just dispatch events
            if (!prevDataRef.current) {
              // First load, set the data
              setPaceMeterData(processedData);
              prevDataRef.current = processedData;
            } else {
              // Check for changes in pace score or category
              if (
                prevDataRef.current.paceScore !== processedData.paceScore ||
                prevDataRef.current.paceCategory !== processedData.paceCategory
              ) {
                // Dispatch event for the update
                dispatchTeacherPaceMeterUpdate(processedData.paceScore, processedData.paceCategory);
                
                // Update the state
                setPaceMeterData(processedData);
                prevDataRef.current = processedData;
              } else {
                // Just update the ref without triggering a re-render
                prevDataRef.current = processedData;
              }
            }
            
          } 
          setError(null);
        } catch (err) {
          console.error('Error processing pace meter data:', err);
        }
      } 
    } catch (err) {
      datadogLogs.logger.error('Error fetching teacher pace meter data', {
        label: 'api-failure',
        api: 'useTeacherPaceMeter',
        classId
      }, err as Error);
      
      setError(err as Error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [classId, paceMeterData, dispatchTeacherPaceMeterUpdate]);

  // Initial fetch
  useEffect(() => {
    fetchPaceMeterData();
  }, [fetchPaceMeterData]);

  // Set up polling
  useEffect(() => {
    if (pollingInterval <= 0) return;

    const intervalId = setInterval(() => {
      fetchPaceMeterData();
    }, pollingInterval);

    return () => clearInterval(intervalId);
  }, [fetchPaceMeterData, pollingInterval]);

  return {
    paceMeterData,
    isLoading,
    error,
    refetch: fetchPaceMeterData,
  };
};

export default useTeacherPaceMeter;